import { App, URLOpenListenerEvent } from "@capacitor/app";
import BRANDS from "@elit-hair/brands";
import { ElitAuthProvider, checkPermissions, useAuth } from "@elit-hair/elit-tools";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SuspenseLoadingScreen } from "@/components/loading-screen";
import { Button } from "@/components/ui/button";
import { ENV } from "@/constants/env";
import { useAppNavigation } from "@/hooks/use-app-navigation";
import { useCapacitor } from "@/hooks/use-capacitor";

const UnauthorizedScreen = () => {
    const { t } = useTranslation();

    const { navigate } = useAppNavigation();
    return (
        <div className="flex flex-col justify-center items-center w-full h-screen bg-elitDarkBlue">
            <h1 className="text-2xl font-bold">{t("unauthorized_page")}</h1>
            <p className="text-lg mb-4">{t("sadly_you_are_not_authorized_to_view_this_page")}</p>
            <Button
                onClick={() => {
                    navigate("/", { replace: true });
                }}
            >
                {t("back_to_home")}
            </Button>
        </div>
    );
};

// For authentication in mobile, check useCapacitor hook
// There is the event triggered by cordova when the app is ready

export const ProtectedRoute = ({ children, permissions }: { children: React.ReactNode; permissions?: string[] }) => {
    const { i18n } = useTranslation();
    const { auth, setAuth, userData } = useAuth();

    const { isCordova } = useCapacitor();
    const [authCode, setAuthCode] = useState<string | undefined>(undefined);

    useEffect(() => {
        const handleUrlOpen = (event: URLOpenListenerEvent) => {
            const url = event.url;
            const urlParams = new URLSearchParams(url.split("?")[1]);
            const code = urlParams.get("code");
            if (code) {
                setAuthCode(code);
            }
        };

        App.addListener("appUrlOpen", handleUrlOpen);

        return () => {
            App.removeAllListeners();
        };
    }, [setAuthCode]);

    return (
        <ElitAuthProvider
            authenticated={!!auth}
            authorized={checkPermissions(userData?.permissionCodes, permissions)}
            customValidationUrl={`${ENV.AUTH_SVC_URL}/auth/sso/login`}
            customRedirectUrl={isCordova ? ENV.APP_URL : undefined}
            loginUrl={ENV.CUSTOM_LOGIN_URL}
            provider="elithair"
            exchangeCodeUrl={`${ENV.AUTH_SVC_URL}/auth/sso/exchange-code`}
            style="D"
            appName="Elit Phone"
            lang={i18n.language}
            onAuthenticated={(accessToken, refreshToken) => {
                setAuth({ accessToken, refreshToken });
                setAuthCode(undefined);
            }}
            redirectingScreen={<SuspenseLoadingScreen />}
            authenticatingScreen={<SuspenseLoadingScreen />}
            unauthorizedScreen={<UnauthorizedScreen />}
            customAuthCode={authCode}
            authServices={[
                {
                    img: BRANDS.elithair.logoSquare || "",
                    color: "#eee",
                    url: ENV.ZOHO_ELITHAIR_SSO_URL,
                },
            ]}
        >
            {children}
        </ElitAuthProvider>
    );
};
